// Turn a parsed dispatch into incident-form values.
//
// The parser returns names ("New York City", "Manhattan", "E205") because that
// is what a dispatch contains. The form's pickers want option objects with the
// database id behind them, so every name has to be matched back against the
// real option lists before it can be filled in. Lists come from optionCache, so
// this reuses whatever the form already fetched instead of asking again.
//
// Matching is deliberately conservative. A name that doesn't clearly match is
// left blank rather than guessed at — a missing field costs the admin a moment,
// a wrong one can misdirect people.
import { fetchOptions } from "elements/System/optionCache";
import { common } from "includes";
import { ParsedDispatch } from "./DispatchIntake";

const norm = (v: any) =>
  String(v || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/** Exact match on the normalised label, else a unique prefix match. Anything
 *  ambiguous returns undefined — see the note above about guessing. */
function matchOption(options: any[], name: string) {
  const target = norm(name);
  if (!target) return undefined;

  const exact = options.find((o) => norm(o.label) === target);
  if (exact) return exact;

  const partial = options.filter(
    (o) => norm(o.label).startsWith(target) || target.startsWith(norm(o.label))
  );
  return partial.length === 1 ? partial[0] : undefined;
}

const toOptions = (rows: any[], get: string, set: string) =>
  common.SetMultiSelect(rows, get, set, undefined);

export type AppliedDispatch = {
  values: Record<string, any>;
  /** The locality option, when one matched — the form needs it to drive the
   *  dependent pickers, not just to display. */
  locality?: any;
  /** Field keys the parser was unsure of, for the review highlight. */
  needsCheck: Record<string, boolean>;
};

export async function applyParsedDispatch(
  parsed: ParsedDispatch,
  auth: string
): Promise<AppliedDispatch> {
  const f = parsed.fields || ({} as any);
  const conf = parsed.confidence || ({} as any);

  const values: Record<string, any> = {};
  const needsCheck: Record<string, boolean> = {};

  // "Unsure" means the parser said so, OR it found a value it couldn't resolve
  // to a real option below. Both deserve the admin's eye.
  const flagIf = (key: string, level: any) => {
    if (level === "low" || level === "medium") needsCheck[key] = true;
  };

  // Plain text fields carry straight across.
  if (f.address) {
    values.address = f.address;
    flagIf("address", conf.address);
  }
  if (f.description) {
    values.field2Value = f.description;
    flagIf("field2Value", conf.description);
  }
  // field3Value is the box number — there is no `box` field on the model.
  if (f.box) {
    values.field3Value = f.box;
    flagIf("field3Value", conf.box);
  }

  // Locality gates everything below it, so resolve it first and bail early if it
  // doesn't match — sub-locality, units and types are all scoped to it.
  const localities = toOptions(
    await fetchOptions("admin/locality", "?offset=1&limit=1000", auth),
    "name",
    "_id"
  );
  const locality = matchOption(localities, f.locality);
  if (!locality) {
    if (f.locality) needsCheck.locality = true;
    return { values, needsCheck };
  }

  values.locality = locality;
  flagIf("locality", conf.locality);

  const subs = toOptions(
    await fetchOptions(
      "admin/sub-locality",
      `?offset=1&limit=1000&query={"locality":["${locality.value}"]}`,
      auth
    ),
    "name",
    "_id"
  );
  const sub = matchOption(subs, f.subLocality);
  if (sub) {
    values.subLocality = sub;
    flagIf("subLocality", conf.subLocality);
  } else if (f.subLocality) {
    needsCheck.subLocality = true;
  }

  // The type field only accepts existing options (it isn't creatable), so an
  // unmatched signal or type is dropped rather than forced in. Signal wins when
  // both are present: "2nd Alarm" is what the apps show as the title.
  const typeOptions = toOptions(
    await fetchOptions(
      `admin/prefilled-field-option/${locality.value}`,
      "?offset=1&limit=1000",
      auth
    ),
    "optionName",
    "optionName"
  );
  const type =
    matchOption(typeOptions, f.signal) || matchOption(typeOptions, f.incidentType);
  if (type) {
    values.field1Value = type;
    flagIf("field1Value", conf.signal || conf.incidentType);
  } else if (f.signal || f.incidentType) {
    needsCheck.field1Value = true;
  }

  // Units are creatable for super admins, but a misread designator is a bad
  // thing to create — only units that already exist are filled in, and anything
  // dropped is flagged so the admin can add it deliberately.
  if (Array.isArray(f.respondingUnits) && f.respondingUnits.length) {
    const unitOptions = toOptions(
      await fetchOptions(
        `admin/prefilled-locality-unit/${locality.value}`,
        "?offset=1&limit=1000",
        auth
      ),
      "unitName",
      "unitName"
    );
    const matched = f.respondingUnits
      .map((u: string) => matchOption(unitOptions, u))
      .filter(Boolean);
    if (matched.length) values.respondingUnits = matched;
    if (matched.length !== f.respondingUnits.length) {
      needsCheck.respondingUnits = true;
    } else {
      flagIf("respondingUnits", conf.respondingUnits);
    }
  }

  return { values, locality, needsCheck };
}
